import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { KnownJsmPractice } from './KnownJsmPractice.types.tsx';

type GetWorkItemRedirectionPathOptions = {
	issueKey: string | undefined;
	projectType: ProjectType | undefined;
	searchQuery: string;
	userPreference?: boolean;
	isEmbed?: boolean;
	wivEnabledForProject?: boolean;
	/**
	 * The recognised JSM `servicedeskPractices` value. Callers pass the Relay
	 * `JiraServiceManagementPractice` enum through `asJsmPractice` first, so unrecognised or future
	 * values arrive here as `null` and fall through to the generic JSM work item route. Only
	 * `INCIDENT_MANAGEMENT` is special-cased; every other recognised practice routes to service requests.
	 */
	jsmPractice: KnownJsmPractice | null;
};

/**
 * Resolves work-item vs JSM redirect from project type, issue key, and current search string.
 *
 * For JSM (service_desk) issues, when the issue's `jsmPractice` is known, the redirect targets the
 * practice-specific route (incidents / service requests) — including its `/embed` variant for
 * embedded views; otherwise it falls back to the generic JSM work item route.
 */
export function getWorkItemRedirectionPath({
	issueKey,
	projectType,
	searchQuery,
	userPreference = true,
	isEmbed = false,
	wivEnabledForProject = true,
	jsmPractice,
}: GetWorkItemRedirectionPathOptions): string | undefined {
	if (!issueKey || !projectType || searchQuery.includes('disableRedirect=true')) {
		return;
	}

	if (['software', 'business'].includes(projectType) && userPreference && wivEnabledForProject) {
		const embedPath = isEmbed ? '/embed' : '';
		return `/jira/browse/${issueKey}${embedPath}${searchQuery}`;
	}

	if (projectType === 'service_desk' && fg('jsm_aniv_route_redirection')) {
		const embedPath = isEmbed ? '/embed' : '';

		// Each practice route is gated behind its own child gate (jsm_aniv_incidents /
		// jsm_aniv_services); when the practice can't be resolved or its gate is off we fall
		// through to the generic JSM work item route below, which classifies the practice
		// itself before rendering. AIWI-159.
		if (jsmPractice) {
			// `jsmPractice` is the `servicedeskPractices` enum value. Incidents get a dedicated
			// route; every other practice (and any unrecognised/future value) uses the service
			// request route. Both expose an `/embed` variant for embedded views.
			if (jsmPractice === 'INCIDENT_MANAGEMENT' && fg('jsm_aniv_incidents')) {
				return `/jira/servicedesk/incidents/${issueKey}${embedPath}${searchQuery}`;
			}
			if (jsmPractice !== 'INCIDENT_MANAGEMENT' && fg('jsm_aniv_services')) {
				return `/jira/servicedesk/service-requests/${issueKey}${embedPath}${searchQuery}`;
			}
		}

		return `/browse/jsm-work-item/${issueKey}${embedPath}${searchQuery}`;
	}
}
